import { lazy, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { useAuth } from '@sine/auth-client';
import { type TrialReport } from '@sine/sine-core-api-types';
import { useTeamSwitcher } from '@sine/sine-team-switcher';
import { Banner } from '@sine/sine-ui-components';

import { authHelpers } from '../../../../authentication/auth.service';
import formatSingularPlural from '../../../../helpers/formatSingularPlural';
import { BillingMode } from '../../../../services/billing/billing.types';
import rxEventHub, { RxEvent } from '../../../../services/events/rx';
import socketEventHub, { ReceiveSocketEvent } from '../../../../services/events/sockets';
import BillingProvider, { useBilling } from '../../../../team/billing/providers/BillingProvider';
import BillingProviderV2 from '../../../../team/billingV2/BillingProvider';
import { useBilling as useBillingV2 } from '../../../../team/billingV2/BillingProvider';
import ManualManageModal from '../../../../team/billingV2/components/ManualManageModal';
import { AclAction } from '../../../../team/permission/constants/actions';
import { useFeatures } from '../../../customHooks/useFeatures';
import useTrialDataQuery from '../../../customHooks/useTrialData.hook';
import wrapLazyLoad from '../../../hoc/wrapLazyLoad';

const LicenseSitesModal = wrapLazyLoad(
  lazy(() => import('../../../../team/billing/unlicencedSites/components/LicenseSitesModal')),
);

const StyledText = styled.p`
  margin: 0;
`;

export default function TrialBanner() {
  const { trialData, refetchTrialData } = useTrialDataQuery();
  const { isImpersonating } = useAuth();
  const { currentTeam } = useTeamSwitcher();

  const { features } = useFeatures();

  const [refetchingTeamPermissions, setRefetchingTeamPermissions] = useState(false);

  useEffect(() => {
    const subscriptions = [
      rxEventHub.on(RxEvent.TEAM_PERMISSIONS_REFETCHING, () => setRefetchingTeamPermissions(true)),
      rxEventHub.on(RxEvent.TEAM_PERMISSIONS_REFRESHED, () => setRefetchingTeamPermissions(false)),
      socketEventHub.on(ReceiveSocketEvent.TEAM_SITELIST, () => refetchTrialData(), false),
      socketEventHub.on(ReceiveSocketEvent.SITE_DELETED, () => refetchTrialData(), false),
    ];

    return () => subscriptions.forEach((sub) => sub.unsubscribe());
  }, []);

  if (!trialData || !Object.entries(trialData).length) return null;

  const isUserTeamAdmin = authHelpers.hasPermission(AclAction.TEAM_ADMIN);

  return !refetchingTeamPermissions && isUserTeamAdmin && currentTeam?.id ? (
    features.includes('BILLING_V2') ? (
      <BillingProviderV2 isImpersonating={isImpersonating} currentTeam={currentTeam}>
        <TrialBannerContent trialData={trialData} isUserTeamAdmin={isUserTeamAdmin} />
      </BillingProviderV2>
    ) : (
      <BillingProvider isUserSystemAdminActing={isImpersonating} currentTeam={currentTeam}>
        <TrialBannerContent trialData={trialData} isUserTeamAdmin={isUserTeamAdmin} />
      </BillingProvider>
    )
  ) : (
    <TrialBannerContent trialData={trialData} isUserTeamAdmin={isUserTeamAdmin} />
  );
}

function TrialBannerContent({ trialData, isUserTeamAdmin }: { trialData: TrialReport; isUserTeamAdmin: boolean }) {
  const { onTrial, numOfUnlicensedSites } = trialData;

  const { billingMode } = useBilling();
  const { isBilledManually, isImpersonating } = useBillingV2();

  const [showLicenseSitesModal, setShowLicenseSitesModal] = useState(false);
  const [showManualManageModal, setShowManualManageModal] = useState(false);

  const message = useMemo(() => generateLicenseWarning(trialData), [trialData]);

  if (!onTrial && !numOfUnlicensedSites) return null;

  const showActionButton = isUserTeamAdmin && billingMode !== BillingMode.OFF && (!isBilledManually || isImpersonating);

  const getActionLabel = () => {
    if (isBilledManually && isImpersonating) {
      return 'Manage manual license';
    }
    return trialData.onTrial
      ? 'License site'
      : `License ${formatSingularPlural(trialData.numOfUnlicensedSites, 'site', 'sites', true)}`;
  };

  const handleAction = () => {
    if (isBilledManually && isImpersonating) {
      setShowManualManageModal(true);
    } else {
      setShowLicenseSitesModal(true);
    }
  };

  return (
    <Banner
      variant='warning'
      data-st-ext='banner-trial'
      action={showActionButton ? getActionLabel() : undefined}
      onAction={handleAction}
    >
      <StyledText>{message}</StyledText>

      {showLicenseSitesModal && (
        <LicenseSitesModal onClose={() => setShowLicenseSitesModal(false)} billableSite={trialData.siteOnTrial} />
      )}
      {showManualManageModal && <ManualManageModal onClose={() => setShowManualManageModal(false)} />}
    </Banner>
  );
}

function generateLicenseWarning(trialData: TrialReport) {
  const { numOfUnlicensedSites, daysLeft, onTrial, siteOnTrial } = trialData;

  if (onTrial && daysLeft) {
    const daysRemaining = formatSingularPlural(daysLeft, 'day', 'days');

    return daysLeft > 0
      ? `${daysRemaining} remaining on your trial.`
      : `Your trial for ${siteOnTrial?.name} has ended.`;
  } else {
    return formatSingularPlural(numOfUnlicensedSites, 'unlicensed site', 'unlicensed sites') + '.';
  }
}
